import { useState } from "react";
import { Link } from "react-router-dom";
import { useJourney } from "@/context/JourneyContext";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { GRADE_FIELDS, savedGradesConfirmPatch, savedGradesToConfirm } from "@/lib/persona";

const DISMISS_KEY = "wua:saved-grades-prompt-dismissed";

// Signed-in: also mark the same fields explicit on the server profile, but only where it
// already holds exactly the value shown. Different or missing values are left alone.
async function markProfileFields(item) {
  const { data } = await api.get("/profile");
  if (!data) return;
  const patch = {};
  if (item.askCurriculum && String(data.curriculum ?? "") === item.curriculum) patch.curriculum = data.curriculum;
  const gk = item.curriculum;
  if (item.askPredicted && GRADE_FIELDS.includes(gk) && String(data[gk] ?? "") === item.predicted) patch[gk] = data[gk];
  const fields = Object.keys(patch);
  if (!fields.length) return;
  await api.post("/profile", { ...patch, explicit_fields: fields, source_surface: "persona_confirm" });
}

function SavedValue({ label, value, confirmed, testid }) {
  return (
    <div className="min-w-0" data-testid={testid}>
      <dt className="text-[10px] uppercase tracking-wider text-stone-500">{label}</dt>
      <dd className="mt-0.5 flex flex-wrap items-center gap-2">
        <span className="font-mono text-[15px] text-stone-900 break-all">{value}</span>
        <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${confirmed ? "bg-emerald-50 text-emerald-800" : "bg-amber-100 text-amber-900"}`}>
          {confirmed ? "Confirmed" : "Saved earlier · not confirmed"}
        </span>
      </dd>
    </div>
  );
}

export function SavedGradesPrompt() {
  const { profile, updateProfile } = useJourney();
  const { user } = useAuth();
  const [hidden, setHidden] = useState(() => sessionStorage.getItem(DISMISS_KEY) === "1");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const item = savedGradesToConfirm(profile);

  if (done) {
    return (
      <p className="mt-3 text-[13px] text-emerald-800" data-testid="persona-saved-grades-thanks" role="status">
        Thanks, confirmed. You can change them any time in Quick Check.
      </p>
    );
  }
  if (!item || hidden) return null;

  const confirm = async () => {
    setBusy(true);
    try {
      await updateProfile(savedGradesConfirmPatch(profile, item));
      if (user) await markProfileFields(item).catch(() => {});
      setDone(true);
    } finally {
      setBusy(false);
    }
  };
  const dismiss = () => { sessionStorage.setItem(DISMISS_KEY, "1"); setHidden(true); };
  const btn = "min-h-[44px] rounded-full px-4 text-[13px] font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[#4C1D95]";

  return (
    <section className="mt-4 rounded-lg border border-amber-200 bg-amber-50/70 p-4" data-testid="persona-saved-grades-prompt" aria-labelledby="saved-grades-q">
      <h3 id="saved-grades-q" className="text-[15px] font-semibold text-stone-900" data-testid="persona-saved-grades-question">{item.question}</h3>
      <p className="mt-1 text-[12px] text-stone-600">
        We have these from an earlier visit, but you never confirmed them, so they aren't in your persona yet.
      </p>
      <dl className="mt-3 grid gap-3 sm:grid-cols-2">
        {item.curriculumLabel && (
          <SavedValue label="Curriculum" value={item.curriculumLabel.replace(/^the /i, "")} confirmed={item.curriculumConfirmed} testid="persona-saved-grades-curriculum" />
        )}
        {item.askPredicted && (
          <SavedValue label={item.noun} value={item.predicted} confirmed={false} testid="persona-saved-grades-predicted" />
        )}
      </dl>
      <div className="mt-4 flex flex-wrap items-center gap-2">
        <button type="button" onClick={confirm} disabled={busy} data-testid="persona-saved-grades-confirm"
          className={`${btn} bg-[#4C1D95] text-white hover:bg-[#3B0764] disabled:opacity-60`}>
          {busy ? "Saving…" : "Yes, that's right"}
        </button>
        <Link to="/admission?draft=saved" data-testid="persona-saved-grades-edit"
          className={`${btn} inline-flex items-center border border-stone-300 bg-white text-stone-800 hover:bg-stone-50`}>
          Edit in Quick Check
        </Link>
        <button type="button" onClick={dismiss} data-testid="persona-saved-grades-dismiss"
          className={`${btn} text-stone-500 hover:text-stone-800 underline underline-offset-2`}>
          Not now
        </button>
      </div>
    </section>
  );
}
